import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Megaphone, Check, AlertOctagon, Loader2, X } from "lucide-react";

/**
 * AnnouncementBlocker — when office posts a notice targeted at the
 * current user's role, this surfaces it as a blocking full-screen modal
 * on first paint, and as a sticky banner once the modal closes (until
 * the user taps "Got it"). Acknowledging stores a per-user record in
 * the backend so office can see who has read what.
 *
 * Polls /api/announcements/pending every 60s in the background so a
 * new notice appears without needing a manual refresh.
 *
 * Multiple pending notices cycle one at a time, urgent first.
 */
export function AnnouncementBlocker() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]); // [{id,title,body,priority,...}]
  const [loaded, setLoaded] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [acking, setAcking] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user || user.role === "super_admin") return;
    try {
      const { data } = await api.get("/announcements/pending");
      setPending(Array.isArray(data) ? data : []);
    } catch { /* offline / not logged in yet */ }
    finally { setLoaded(true); }
  }, [user]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // Background refresh every 60s while the tab is open — pulls in new posts from office.
  usePoll(fetchPending, 60000, { enabled: !!user, immediate: false });

  // When pending list changes, reset the modal cursor + banner.
  useEffect(() => {
    setModalIdx(0);
    setBannerDismissed(false);
  }, [pending.length]);

  if (!loaded || !user || user.role === "super_admin") return null;
  if (pending.length === 0) return null;

  const current = pending[modalIdx] || pending[0];
  if (!current) return null;

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post(`/announcements/${current.id}/acknowledge`);
      // Remove from pending immediately for snappy UX
      setPending((prev) => prev.filter((a) => a.id !== current.id));
      setModalIdx(0);
    } catch { /* surface via banner re-display */ }
    finally { setAcking(false); }
  };

  // Field workers can't dismiss the modal without acknowledging; office
  // (if targeted) can close it but the banner remains.
  const canSoftDismiss = user.role !== "field" && user.role !== "mechanic";

  return (
    <>
      <Dialog open={!bannerDismissed} onOpenChange={(v) => { if (!v && canSoftDismiss) setBannerDismissed(true); }}>
        <DialogContent
          hideClose={!canSoftDismiss}
          className={`max-w-md p-0 border-2 rounded-sm overflow-hidden ${
            current.priority === "urgent"
              ? "border-red-500 dark:border-red-600"
              : "border-[#FF5F15] dark:border-[#FF5F15]"
          }`}
          data-testid="announcement-modal"
          onPointerDownOutside={(e) => { if (!canSoftDismiss) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (!canSoftDismiss) e.preventDefault(); }}
        >
          <div
            className={`px-5 py-3 flex items-center gap-2 text-white font-display font-black uppercase tracking-widest text-[12px] ${
              current.priority === "urgent" ? "bg-red-600" : "bg-[#FF5F15]"
            }`}
            data-testid="announcement-modal-header"
          >
            {current.priority === "urgent" ? <AlertOctagon className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
            {current.priority === "urgent" ? "// URGENT NOTICE FROM OFFICE" : "// MESSAGE FROM OFFICE"}
          </div>
          <div className="bg-white dark:bg-[#0B1018] p-5">
            <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">
              {current.created_by_name || "Office"} · {(current.created_at || "").slice(0, 10)}
              {pending.length > 1 && (
                <span className="ml-2 text-[#FF5F15] font-bold">
                  · {modalIdx + 1} of {pending.length}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2.5" data-testid="announcement-modal-title">
              {current.title}
            </h2>
            <div
              className="text-[14px] text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-[40dvh] overflow-y-auto"
              data-testid="announcement-modal-body"
            >
              {current.body}
            </div>
            <div className="flex items-center gap-2 mt-5">
              {pending.length > 1 && modalIdx < pending.length - 1 && (
                <Button
                  variant="outline"
                  className="rounded-sm flex-1"
                  data-testid="announcement-modal-next"
                  onClick={() => setModalIdx((i) => Math.min(i + 1, pending.length - 1))}
                >
                  Read next →
                </Button>
              )}
              <Button
                className="rounded-sm flex-1 bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold"
                data-testid="announcement-modal-ack"
                disabled={acking}
                onClick={acknowledge}
              >
                {acking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                Got it
              </Button>
              {canSoftDismiss && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-sm shrink-0"
                  data-testid="announcement-modal-dismiss"
                  onClick={() => setBannerDismissed(true)}
                  title="Hide for now (still showing in banner)"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sticky banner — shown after the modal is dismissed but still pending. */}
      {bannerDismissed && pending.length > 0 && (
        <button
          type="button"
          onClick={() => setBannerDismissed(false)}
          data-testid="announcement-banner"
          className={`w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left text-white font-semibold text-[13px] hover:brightness-110 transition ${
            current.priority === "urgent" ? "bg-red-600" : "bg-[#FF5F15]"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {current.priority === "urgent" ? <AlertOctagon className="w-4 h-4 shrink-0" /> : <Megaphone className="w-4 h-4 shrink-0" />}
            <span className="font-display font-black uppercase tracking-widest text-[11px] shrink-0">
              {pending.length > 1 ? `${pending.length} unread notices` : "Unread notice from office"}
            </span>
            <span className="truncate opacity-90">— {current.title}</span>
          </div>
          <span className="font-bold uppercase tracking-widest text-[10px] border border-white/40 rounded-sm px-2 py-0.5 shrink-0">
            Tap to read
          </span>
        </button>
      )}
    </>
  );
}
